import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
    WxbAlert,
    WxbButton,
    WxbEmpty,
    WxbIcon,
    WxbModal,
    WxbSpinner,
    WxbTag,
    wxbToast,
} from '../wxb-ui';
import {
    batchPlanApi,
    type BatchRefreshChangedOp,
    type BatchRefreshOp,
    type BatchRefreshPreview,
    type BatchRefreshRemovedOp,
} from '../../services/api';
import {
    canReplaceBatchTemplate,
    hasRelatedBatchRefreshChanges,
} from './batchRefreshUtils';

interface RefreshFromTemplateModalProps {
    batchId: number | null;
    visible: boolean;
    onClose: () => void;
    onApplied: () => void;
}

const fmtDateTime = (value: string | number | null | undefined): string =>
    value === null || value === undefined || value === '' ? '—' : dayjs(value).format('MM-DD HH:mm');

const fmtFieldValue = (field: string, value: string | number | null): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (field.includes('datetime')) return fmtDateTime(value);
    return String(value);
};

const opLabel = (op: { stage_name: string | null; operation_name: string | null; operation_code: string | null }) => {
    const main = op.operation_name || op.operation_code || '未命名操作';
    return op.stage_name ? `${op.stage_name} · ${main}` : main;
};

const RefreshFromTemplateModal: React.FC<RefreshFromTemplateModalProps> = ({
    batchId,
    visible,
    onClose,
    onApplied,
}) => {
    const [preview, setPreview] = useState<BatchRefreshPreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const changeCount = preview
        ? preview.added.length + preview.removed.length + preview.changed.length
        : 0;
    const hasRelatedChanges = preview ? hasRelatedBatchRefreshChanges(preview) : false;
    const hasAnyChanges = changeCount > 0 || hasRelatedChanges;

    useEffect(() => {
        if (!visible || !batchId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setPreview(null);
        batchPlanApi
            .refreshPreview(batchId)
            .then((data) => {
                if (cancelled) return;
                setPreview(data);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.response?.data?.error || '生成刷新预览失败');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, batchId]);

    const handleApply = useCallback(async () => {
        if (!batchId || !preview?.canRefresh) return;
        setApplying(true);
        try {
            const result = await batchPlanApi.refresh(batchId, {
                previewRevision: preview.revision,
            });
            const overrideCopy = result.overridesCleared > 0
                ? `，清理 ${result.overridesCleared} 条共享组覆盖`
                : '';
            const manualShareCopy = result.manualShareMembershipsCleared > 0
                ? `，清理 ${result.manualShareMembershipsCleared} 条人工共享成员关系`
                : '';
            wxbToast.success(
                `已重新替换模版：重建 ${result.replaced} 个工序${overrideCopy}${manualShareCopy}`,
            );
            onApplied();
        } catch (err: any) {
            wxbToast.error(err?.response?.data?.error || '从模版刷新失败');
        } finally {
            setApplying(false);
        }
    }, [batchId, preview, onApplied]);

    const blocked = preview ? !preview.canRefresh : false;
    const canApply = !!preview && canReplaceBatchTemplate(preview);

    const footer = (
        <div className="batch-refresh-modal__footer">
            <WxbButton variant="ghost" onClick={onClose}>
                {canApply ? '取消' : '关闭'}
            </WxbButton>
            {canApply && (
                <WxbButton variant="primary" onClick={handleApply} disabled={applying}>
                    {applying ? '替换中...' : '重新替换模版'}
                </WxbButton>
            )}
        </div>
    );

    return (
        <WxbModal
            open={visible}
            title="重新替换批次模版"
            onCancel={onClose}
            footer={footer}
            width={680}
            centered
        >
            <div className="batch-refresh-modal__content">
                {loading && <WxbSpinner tip="正在对比模版差异..." />}

                {!loading && error && (
                    <WxbEmpty description={error} />
                )}

                {!loading && !error && preview && (
                    <>
                        <div className="batch-refresh-modal__batch">
                            批次 <strong>{preview.batch.batch_code}</strong>
                            {preview.batch.template_name ? ` · 模版「${preview.batch.template_name}」` : ''}
                            {preview.batch.day0_date ? ` · Day0 ${preview.batch.day0_date}` : ''}
                        </div>

                        {blocked && (
                            <WxbEmpty
                                image={<WxbIcon name="quarantine" size={40} />}
                                description={preview.blockReason || '该批次当前不可刷新'}
                            />
                        )}

                        {!blocked && (
                            <WxbAlert
                                title="完整替换当前模板快照"
                                className="batch-refresh-modal__alert"
                            >
                                将先清空模板派生快照、批次级共享组覆盖，以及该批次工序参与的人工共享关系，
                                再按当前模板完整重建。独立工序本身保留。
                            </WxbAlert>
                        )}

                        {!blocked && !hasAnyChanges && (
                            <WxbEmpty
                                image={<WxbIcon name="release" size={40} />}
                                description="差异预览未发现变化；仍可执行完整替换，用于清理未被预览识别的历史残留。"
                            />
                        )}

                        {!blocked && hasAnyChanges && (
                            <>
                                <div className="batch-refresh-modal__summary">
                                    新增 {preview.summary.added} · 变更 {preview.summary.changed} · 移除 {preview.summary.removed} · 未变 {preview.summary.unchanged}
                                    {preview.summary.locked > 0 ? ` · 锁定 ${preview.summary.locked}（仍会重建）` : ''}
                                </div>

                                {hasRelatedChanges && (
                                    <div className="batch-refresh-modal__row">
                                        <WxbTag color="blue">关系重建</WxbTag>
                                        <div className="batch-refresh-modal__row-body">
                                            <div className="batch-refresh-modal__row-title">模版派生关系有更新</div>
                                            <div className="batch-refresh-modal__row-meta">
                                                {[
                                                    preview.relatedChanges.constraints && '操作约束',
                                                    preview.relatedChanges.shareGroups && '人员共享组',
                                                    preview.relatedChanges.resourceRules && '资源规则',
                                                ].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 新增工序 */}
                                {preview.added.map((op: BatchRefreshOp) => (
                                    <div key={`add-${op.template_schedule_id}`} className="batch-refresh-modal__row">
                                        <WxbTag color="green">新增</WxbTag>
                                        <div className="batch-refresh-modal__row-body">
                                            <div className="batch-refresh-modal__row-title">{opLabel(op)}</div>
                                            <div className="batch-refresh-modal__row-meta">
                                                {fmtDateTime(op.planned_start_datetime)} 开始 · {op.required_people ?? '—'} 人
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* 变更工序 */}
                                {preview.changed.map((op: BatchRefreshChangedOp) => (
                                    <div key={`chg-${op.template_schedule_id}`} className="batch-refresh-modal__row">
                                        <WxbTag color="amber">变更</WxbTag>
                                        <div className="batch-refresh-modal__row-body">
                                            <div className="batch-refresh-modal__row-title">
                                                {opLabel(op)}
                                                {op.is_locked && (
                                                    <WxbTag color="neutral" className="batch-refresh-modal__inline-tag">
                                                        锁定 · 将重建
                                                    </WxbTag>
                                                )}
                                            </div>
                                            <div className="batch-refresh-modal__row-meta">
                                                {op.fields.map((f) => (
                                                    <span key={f.field} className="batch-refresh-modal__field">
                                                        {f.label}：
                                                        <span className="batch-refresh-modal__field-from">{fmtFieldValue(f.field, f.from)}</span>
                                                        {' → '}
                                                        <span className="batch-refresh-modal__field-to">{fmtFieldValue(f.field, f.to)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* 移除工序 */}
                                {preview.removed.map((op: BatchRefreshRemovedOp) => (
                                    <div key={`del-${op.template_schedule_id}`} className="batch-refresh-modal__row">
                                        <WxbTag color="red">移除</WxbTag>
                                        <div className="batch-refresh-modal__row-body">
                                            <div className="batch-refresh-modal__row-title">
                                                {opLabel(op)}
                                                {op.is_locked && (
                                                    <WxbTag color="neutral" className="batch-refresh-modal__inline-tag">
                                                        锁定 · 仍会移除
                                                    </WxbTag>
                                                )}
                                            </div>
                                            <div className="batch-refresh-modal__row-meta">
                                                该操作已从模版删除，将随旧模板快照一并移除
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}
            </div>
        </WxbModal>
    );
};

export default RefreshFromTemplateModal;
